import { useAppSelector } from "../../../../../utils/store";
import React from "react";
import {
  ApiCampaignData,
  CampaignState,
  CampaignType,
} from "../../../../../core/orderAd/orderAd.entity";
import { Row } from "../DetailCard";
import styles from "../OrderDetails.module.scss";
import { strings } from "../../../../../intl/fa";
import { copyTextToClipboard } from "../../../../../utils/clipboard";
import { Copy } from "@bale/ui";
import { formatStrings } from "../../../../../utils/utils";
import { toast } from "react-toastify";
import { campaignLabels } from "../../ordersList/tabs/campaignOrders/campaignItem/CampaignItem";
import { digitsToHindi, percentCalc, toMoneyFormat } from "@bale/utils";

const getIcon = (state: CampaignState) => {
  const base = "/icons/adState/";
  switch (state) {
    case CampaignState.AwaitingApproval:
      return base + "wait_to_approve.svg";
    case CampaignState.Active:
      return base + "viewing.svg";
    case CampaignState.Finished:
      return base + "done.svg";
    case CampaignState.Canceled:
    case CampaignState.Rejected:
      return base + "rejected.svg";
  }
};

type CampaignProps = {
  campaign: ApiCampaignData;
};

const CampaignDetailCard: React.FC<React.PropsWithChildren<CampaignProps>> = ({ campaign }) => {
  const adRecord = campaign.ads[0];
  if (!adRecord) return null;

  function adIdCopyHandler() {
    toast.info(
      strings.order_details.orderIdCopied.replace("{value}", strings.campaign)
    );
    copyTextToClipboard(campaign.id);
  }

  const showDetails = [
    CampaignState.Active,
    CampaignState.Paused,
    CampaignState.Finished,
  ].includes(campaign.state);

  return (
    <div className={styles.Container}>
      <div className={styles.Card}>
        <div className={styles.HeaderWrapper}>
          <div className={styles.LabelWrapper}>
            <img src={getIcon(campaign.state)} alt="" />
            <p className={styles.Label}>
              {campaignLabels[campaign.state]?.label}
            </p>
          </div>
          <div className={styles.CopyWrapper} onClick={adIdCopyHandler}>
            <Copy size={20} />
            <p>{strings.campaign_details.ad_id}</p>
          </div>
        </div>
        <div className={styles.InfoWrapper}>
          {campaign.state === CampaignState.Rejected && (
            <div className={styles.rejectionReason}>
              {formatStrings(strings.campaign_details.rejection_message, {
                reason: campaign.rejection_reason,
              })}
            </div>
          )}
          <Row
            rowKey={strings.campaign_details.title}
            value={
              adRecord.title
                ? adRecord.title
                : strings.campaign_details.without_title
            }
          />
          <Row
            rowKey={strings.campaign_details.description}
            value={adRecord.description}
          />
          <Row
            rowKey={strings.campaign_details.ad_link}
            value={adRecord.link}
            dir={"ltr"}
          />
          <Row
            rowKey={strings.campaign_details.campaign_type}
            value={
              adRecord.type === CampaignType.ViewBased
                ? strings.viewy
                : strings.clicky
            }
          />
          {showDetails && (
            <>
              <Row
                rowKey={strings.campaign_details.view_count}
                value={digitsToHindi(
                  toMoneyFormat(String(adRecord.view_count))
                )}
              />
              <Row
                rowKey={strings.campaign_details.click_count}
                value={digitsToHindi(
                  toMoneyFormat(String(adRecord.click_count))
                )}
              />
              <Row
                rowKey={strings.campaign_details.ctr}
                value={digitsToHindi(
                  percentCalc(adRecord.click_count, adRecord.view_count)
                )}
              />
              <Row
                rowKey={strings.campaign_details.cost}
                value={
                  digitsToHindi(toMoneyFormat(String(adRecord.cost))) +
                  " " +
                  strings.common.rial
                }
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const CampaignDetails: React.FC<React.PropsWithChildren<unknown>> = () => {
  const selectedCampaign = useAppSelector(
    (state) => state.ordersList.selectedCampaign
  );
  if (!selectedCampaign) return null;

  return <CampaignDetailCard campaign={selectedCampaign} />;
};
